/**
 * Record an approval given outside CreativeOS (chapter 08 frame FKvo5, backend
 * #129): who approved, how, when and a note. The post moves on as if they had
 * pressed Approve, and the post says so ("Approved by Noura on WhatsApp").
 *
 * Not drawn, for want of backend fields: the Pen's Frame.io channel (the API
 * takes WhatsApp, email, a call or in person) and the Proof attachment.
 */
import { useMemo, useState } from "react";
import { Check } from "lucide-react";
import type { Key } from "react-aria-components";
import { KitButton } from "@/app/routes/dashboard/components/kit/Button";
import { KitSelect, KitSelectItem } from "@/app/routes/dashboard/components/kit/Select";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { useGetBrands } from "@/hooks/useGetBrands";
import { useWorkspaceStore } from "@/stores/workspaceStore";
import { useAuthStore } from "@/stores/authStore";
import { cn } from "@/utils/helpers";
import type { OutsideApprovalChannel } from "@/types/contentCalendar.type";
import { useClientNotifyState } from "../api/useContentCalendar";
import { DayField, DialogLabel, Initials, ReviewDialog, localTodayIso } from "./reviewDialogParts";
import { useRecordOutsideApproval, type ReviewRoundKind } from "./useClientRoundActions";

export const CHANNEL_LABEL: Record<OutsideApprovalChannel, string> = {
  whatsapp: "WhatsApp",
  email: "Email",
  call: "Call",
  in_person: "In person",
};
const CHANNELS: OutsideApprovalChannel[] = ["whatsapp", "email", "call", "in_person"];
const SOMEONE_ELSE = "__someone-else__";

/** "Approved by Noura on WhatsApp" — the line a recorded approval reads as. */
export function outsideApprovalLine(name: string, channel: OutsideApprovalChannel, recordedBy?: string | null): string {
  const how = channel === "in_person" ? "in person" : channel === "call" ? "on a call" : `on ${CHANNEL_LABEL[channel]}`;
  return `Approved by ${name} ${how}${recordedBy ? `, recorded by ${recordedBy}` : ""}`;
}

/** The moment to record: now for today, else midday of the day picked. */
function approvedAtFor(day: string): string {
  if (day === localTodayIso()) return new Date().toISOString();
  const [y, m, d] = day.split("-").map(Number);
  return new Date(y!, m! - 1, d!, 12, 0, 0).toISOString();
}

export default function RecordApprovalDialog({
  isOpen,
  onOpenChange,
  planId,
  slotId,
  kind,
  version,
  clientName,
}: {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  planId: string | null;
  slotId: string;
  /** Which send it answers: the concept, or the creative brief. */
  kind: ReviewRoundKind;
  /** The version the client approved, for the description. */
  version?: number | null;
  /** The client (the brand's name); read from the open brand when omitted. */
  clientName?: string | null;
}) {
  const phone = useMediaQuery("(max-width: 767px)");
  const notifyState = useClientNotifyState(planId, isOpen);
  const record = useRecordOutsideApproval(planId);
  const recorder = useAuthStore((s) => s.user?.name)?.trim() || null;
  const selectedBrandId = useWorkspaceStore((s) => s.selectedBrandId);
  const { data: brands } = useGetBrands();
  const client =
    clientName?.trim() || brands?.find((brand) => brand.id === selectedBrandId)?.name?.trim() || "The client";

  const guests = useMemo(
    () =>
      (notifyState.data?.guests ?? []).map((guest) => ({
        id: guest.userId,
        name: guest.name?.trim() || guest.email,
      })),
    [notifyState.data?.guests],
  );

  const [who, setWho] = useState<string>("");
  const [otherName, setOtherName] = useState("");
  const [channel, setChannel] = useState<OutsideApprovalChannel>("whatsapp");
  const [day, setDay] = useState(localTodayIso());
  const [note, setNote] = useState("");

  // The dialog mounts fresh on each open (see ClientRoundActions); the first
  // client stands picked until another is chosen.
  const picked = who || guests[0]?.id || "";

  const someoneElse = picked === SOMEONE_ELSE || (guests.length === 0 && !notifyState.isLoading);
  const name = someoneElse ? otherName.trim() : (guests.find((guest) => guest.id === picked)?.name ?? "");
  const ready = !!name && !!day;

  const submit = () => {
    if (!ready) return;
    // Optimistic: the dialog closes on confirm; a failure rolls back with a toast.
    record.mutate({ slotId, kind, name, channel, approvedAt: approvedAtFor(day), note: note.trim() || null });
    onOpenChange(false);
  };

  const what = kind === "concept" ? "concept" : "creative brief";
  const description = `${version ? `Version ${version} · ` : ""}${what}. ${client} approved it outside CreativeOS; record it and the post moves on, as if they had pressed Approve.`;

  const row = (label: string, field: React.ReactNode, top = false) => (
    <div className={cn("flex gap-4", phone ? "flex-col gap-2" : top ? "items-start" : "items-center")}>
      <span className={cn("shrink-0", !phone && "w-[110px]", top && !phone && "flex h-8 items-center")}>
        <DialogLabel phone={phone}>{label}</DialogLabel>
      </span>
      <div className="min-w-0 flex-1">{field}</div>
    </div>
  );

  const fieldClass =
    "co-input-own-focus co-glass-control h-11 w-full rounded-card px-3.5 text-[14px] text-co-text caret-co-accent placeholder:text-co-faint outline-offset-0 focus:outline-2 focus:outline-co-accent";

  const footer = phone ? (
    <div className="shrink-0 px-4 pb-4 pt-2">
      <KitButton size="lg" className="h-12 w-full" onClick={submit} isDisabled={!ready}>
        Record approval
      </KitButton>
    </div>
  ) : (
    <div className="flex shrink-0 items-center justify-end gap-3 px-6 pb-6 pt-4">
      <KitButton variant="ghost" onClick={() => onOpenChange(false)}>
        Cancel
      </KitButton>
      <KitButton onClick={submit} isDisabled={!ready}>
        Record approval
      </KitButton>
    </div>
  );

  return (
    <ReviewDialog
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      title="Record an approval"
      description={description}
      phone={phone}
      width={560}
      footer={footer}
    >
      <div className={cn("flex flex-col gap-[18px]", phone ? "p-4" : "px-6 pb-5 pt-2")}>
        {row(
          "Who approved",
          <div className="flex flex-col gap-2">
            {notifyState.isLoading ? (
              <span aria-label="Loading clients" className="block h-11 animate-pulse rounded-pill bg-co-tonal" />
            ) : guests.length > 0 ? (
              <KitSelect
                aria-label="Who approved"
                selectedKey={picked || null}
                onSelectionChange={(key: Key | null) => setWho(key ? String(key) : "")}
              >
                {[
                  ...guests.map((guest) => (
                    <KitSelectItem key={guest.id} id={guest.id} textValue={guest.name}>
                      <span className="flex min-w-0 items-center gap-2.5">
                        <Initials name={guest.name} size={20} fontSize={9} />
                        <span className="truncate">
                          {guest.name}
                          {client !== "The client" ? ` · ${client}` : ""}
                        </span>
                      </span>
                    </KitSelectItem>
                  )),
                  <KitSelectItem key={SOMEONE_ELSE} id={SOMEONE_ELSE} textValue="Someone else">
                    Someone else
                  </KitSelectItem>,
                ]}
              </KitSelect>
            ) : null}
            {someoneElse ? (
              <input
                value={otherName}
                maxLength={120}
                onChange={(event) => setOtherName(event.target.value)}
                placeholder="Their name"
                aria-label="Their name"
                className={fieldClass}
              />
            ) : null}
          </div>,
        )}
        {row(
          "How",
          <div role="radiogroup" aria-label="How they approved" className="flex flex-wrap gap-2">
            {CHANNELS.map((option) => {
              const picked = channel === option;
              return (
                <button
                  key={option}
                  type="button"
                  role="radio"
                  aria-checked={picked}
                  onClick={() => setChannel(option)}
                  className={cn(
                    "flex h-8 items-center gap-2 rounded-pill px-3 text-[13px] font-medium text-co-text outline-none transition-colors",
                    "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-co-accent",
                    picked ? "bg-co-accent-dim shadow-[inset_0_0_0_1px_var(--co-accent-border)]" : "co-glass-control",
                  )}
                >
                  {CHANNEL_LABEL[option]}
                  {picked ? <Check aria-hidden className="size-3.5 text-co-accent" strokeWidth={2} /> : null}
                </button>
              );
            })}
          </div>,
          true,
        )}
        {row(
          "When",
          <DayField
            label="When they approved"
            value={day}
            onChange={setDay}
            maxValue={localTodayIso()}
            showToday
            className={phone ? "w-full" : "w-fit min-w-[186px]"}
          />,
        )}
        {row(
          "Note",
          <input
            value={note}
            maxLength={500}
            onChange={(event) => setNote(event.target.value)}
            placeholder="Optional, e.g. “Approved on the WhatsApp group”"
            aria-label="Note"
            className={fieldClass}
          />,
        )}
        <p className="text-[12.5px] leading-[1.45] text-co-muted">
          Recorded approvals say so on the post: “{outsideApprovalLine(name || client, channel, recorder)}.”
        </p>
      </div>
    </ReviewDialog>
  );
}
